import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneVerificationModal from "../Component/AptitudeTest/PhoneVerificationModal";
import OnboardingModal from "../Component/AptitudeTest/OnboardingModal";
import AuthFlowModal, { needsAuthFlow } from "../Component/Auth/AuthFlowModal";
import TestLanding from "../Component/TestLanding";
import TestArt from "../Component/TestIntro/TestArt";
import { readTtLang } from "../utils/ttLang";
import "../Assets/Styles/landing-page.css";

const LandingPage = () => {
  // Follows the Tests & Tools page's language chip (utils/ttLang): an English
  // hub opens an English landing page, an Arabic hub an Arabic one.
  const [language, setLanguage] = useState(readTtLang);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [phoneData, setPhoneData] = useState(null);
  const [showRecaptchaBadge, setShowRecaptchaBadge] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent body scroll when modal is open
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (showPhoneModal || showOnboarding) {
      // Store current scroll position
      const scrollY = window.scrollY;
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
      body.style.overflow = "hidden";
      html.style.overflow = "hidden";
    } else {
      // Restore scroll position
      const scrollY = body.style.top;
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflow = "";
      html.style.overflow = "";

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflow = "";
      html.style.overflow = "";
    };
  }, [showPhoneModal, showOnboarding]);

  // Show badge only on LandingPage AND when phone modal is open
  useEffect(() => {
    const isLandingPage = location.pathname === "/aptitude-test";

    if (isLandingPage && showPhoneModal) {
      setShowRecaptchaBadge(true);
    } else {
      setShowRecaptchaBadge(false);
    }
  }, [showPhoneModal, location.pathname]);

  // Cleanup badge when component unmounts
  useEffect(() => {
    return () => {
      setShowRecaptchaBadge(false);
    };
  }, []);

  const handleStartTest = () => {
    // Sign in first: phone + OTP, and — for a first-time student — the common
    // onboarding questions (name, DOB, nationality, destination, degree).
    // A signed-in student with a complete profile goes straight to the
    // test-specific onboarding; the shared answers are skipped by fill-once.
    if (needsAuthFlow()) setShowAuth(true);
    else setShowOnboarding(true);
  };

  const handlePhoneVerification = (verifiedPhoneData) => {
    console.log("Phone verification completed:", verifiedPhoneData);
    setPhoneData(verifiedPhoneData);
    setShowPhoneModal(false);

    setTimeout(() => {
      setShowOnboarding(true);
    }, 100);
  };

  const handleModalClose = () => {
    setShowPhoneModal(false);
  };

  const handleOnboardingComplete = (data) => {
    // phoneData is null in the new flow (verification happens after the
    // quiz, inside the results submission) — keep every field null-safe.
    const testData = {
      ...data,
      phone: phoneData?.phone || "",
      verified: Boolean(phoneData),
      timestamp: phoneData?.timestamp || Date.now(),
    };

    localStorage.setItem("aptitudeTestData", JSON.stringify(testData));
    if (phoneData) {
      localStorage.setItem("verifiedPhone", JSON.stringify(phoneData));
    }

    navigate("/aptitude-test-quiz");
  };

  return (
    <>
      {/* Main Landing Page — IELTS landing structure, aptitude content */}
      <div
        style={{
          position: showPhoneModal || showOnboarding ? "fixed" : "relative",
          width: "100%",
          left: 0,
          right: 0,
        }}
        className="landing-page"
      >
        <TestLanding
          dir={language === "ar" ? "rtl" : "ltr"}
          badge={language === "ar" ? "اختبار مجاني ومصغر أقل من 20 دقيقة" : "Free mini mock test — under 20 minutes"}
          title={
            language === "ar" ? (
              <>
                الاختبار <span className="tland__hl">الوطني</span> التجريبي
              </>
            ) : (
              <>
                The <span className="tland__hl">National</span> Mock Test
              </>
            )
          }
          subtitle={
            language === "ar" ? (
              <>
                أسرع طريقة لمعرفة درجتك <b>التقريبية</b> — الاختبار عبارة عن 15
                سؤالاً في قسم <b>الإنجليزي</b> و5 أسئلة في قسم <b>الرياضيات</b>.
              </>
            ) : (
              <>
                The fastest way to see your <b>estimated</b> score — 15
                questions in the <b>English</b> section and 5 in <b>Maths</b>.
              </>
            )
          }
          ctaLabel={language === "ar" ? "ابدأ الاختبار" : "Start the test"}
          onStart={handleStartTest}
          imageNode={<TestArt kind="mock" />}
        />

        {/* Phone Verification Modal */}
        {showPhoneModal && (
          <PhoneVerificationModal
            language={language}
            onVerificationComplete={handlePhoneVerification}
            onClose={handleModalClose}
          />
        )}

        {/* Sign-in gate (phone OTP + first-time profile) */}
        {showAuth && (
          <AuthFlowModal
            language={language}
            onHide={() => setShowAuth(false)}
            onDone={() => { setShowAuth(false); setShowOnboarding(true); }}
          />
        )}

        {/* Onboarding Modal */}
        {showOnboarding && (
          <OnboardingModal
            language={language}
            onComplete={handleOnboardingComplete}
            onClose={() => setShowOnboarding(false)}
          />
        )}
      </div>

      {/* Global reCAPTCHA Badge - ONLY on LandingPage */}
      {showRecaptchaBadge && (
        <div className="recaptcha-badge landing-page-recaptcha">
          <div className="recaptcha-badge-content">
            <span className="recaptcha-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#4285F4">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
              </svg>
            </span>
            <span className="recaptcha-text">
              {language === "ar"
                ? "محمي بواسطة reCAPTCHA"
                : "Protected by reCAPTCHA"}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingPage;
